import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  CheckCircle,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettings } from '../hooks/useSettings';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatPhone } from '../utils/formatters';
import { trackLead } from '../utils/tracking';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CONTACT_AREAS } from '../utils/constants';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitType, setSubmitType] = useState<'email' | 'whatsapp' | null>(null);
  const [formStartTime, setFormStartTime] = useState<number>(Date.now());

  const businessHoursText = (() => {
    let hours = settings?.business_hours || 'Segunda à Sexta das 9h às 18h';
    if (hours.startsWith('"') && hours.endsWith('"')) {
      hours = hours.slice(1, -1);
    }
    return hours;
  })();

  const contactSchema = z.object({
    name: z.string().min(3, { message: t('contact.form.validation.required') }),
    phone: z.string().min(14, { message: t('contact.form.validation.invalidPhone') }),
    email: z.string().email({ message: t('contact.form.validation.invalidEmail') }),
    area: z.string().min(1, { message: t('contact.form.validation.required') }),
    situation: z.string().min(10, { message: t('contact.form.validation.required') }),
    consent: z.literal(true, {
        message: t('contact.form.validation.required')
    }),
    honeypot: z.string().max(0, "Spam").optional()
  });

  type ContactFormData = z.infer<typeof contactSchema>;

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      area: '',
      situation: '',
      honeypot: ''
    }
  });

  const selectedArea = watch('area');

  useEffect(() => {
    setFormStartTime(Date.now());
  }, [step]);

  const handleNextStep = async () => {
    let isValid = false;
    
    if (step === 1) {
      isValid = await trigger(['name', 'phone', 'email']);
    } else if (step === 2) {
      isValid = await trigger(['area']);
      if (!selectedArea) {
        toast.error(t('contact.form.validation.required'));
        return;
      }
    }

    if (isValid) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => setStep(prev => prev - 1);

  const onSubmit = async (data: ContactFormData, e?: React.BaseSyntheticEvent, tipoEnvio: 'email' | 'whatsapp' = 'email') => {
    e?.preventDefault();

    if (data.honeypot) {
      toast.error(t('contact.form.validation.spam'));
      return;
    }
    
    if (Date.now() - formStartTime < 3000) {
      toast.error(t('contact.form.validation.spam'));
      return;
    }

    setIsSubmitting(true);
    setSubmitType(tipoEnvio);
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://luararezende.adv.br/api';
      const endpoint = apiBaseUrl.includes('webhook/api') 
          ? `${apiBaseUrl}/contato` 
          : `${apiBaseUrl}/contact`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: data.name,
          phone: data.phone,
          email: data.email,
          city: 'Não informado', // Cidade foi unificada
          situation: `[ÁREA: ${data.area}] - ${data.situation}`,
          timestamp: new Date().toISOString(),
          source: 'website',
          tipoEnvio: tipoEnvio
        })
      });

      if (!response.ok) {
          if (response.status === 429) {
              toast.error(t('contact.form.validation.rateLimit'));
          } else {
              throw new Error(`Erro ${response.status}`);
          }
          return;
      }
      
      trackLead(); // Dispara o evento de CRO
      setSubmitStatus('success');
      toast.success(t('contact.form.success'));
      setStep(4);
      reset(); // Limpa o form
      setFormStartTime(Date.now());
    } catch (error) {
      console.error('Erro:', error);
      setSubmitStatus('error');
      toast.error(t('contact.form.error'));
    } finally {
      setIsSubmitting(false);
      setSubmitType(null);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} key="step1">
            <h4 className="text-xl font-heading text-brand-slate mb-4">Passo 1: Seus dados para contato</h4>
            <div className="space-y-4 mb-6">
              <div>
                <input 
                  type="text" 
                  {...register('name')}
                  aria-invalid={errors.name ? 'true' : 'false'}
                  className={`input w-full ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`} 
                  placeholder="Seu nome completo *" 
                  aria-label="Seu nome completo"
                />
                {errors.name && <span className="text-red-500 text-sm mt-1 block">{errors.name.message}</span>}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <input 
                          type="tel" 
                          {...field}
                          onChange={(e) => field.onChange(formatPhone(e.target.value))}
                          aria-invalid={errors.phone ? 'true' : 'false'}
                          className={`input w-full ${errors.phone ? 'border-red-500 focus:ring-red-500' : ''}`} 
                          placeholder="WhatsApp *" 
                          aria-label="WhatsApp"
                        />
                      )}
                    />
                    {errors.phone && <span className="text-red-500 text-sm mt-1 block">{errors.phone.message}</span>}
                  </div>
                  <div>
                    <input 
                      type="email" 
                      {...register('email')}
                      aria-invalid={errors.email ? 'true' : 'false'}
                      className={`input w-full ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`} 
                      placeholder="Email *" 
                      aria-label="E-mail"
                    />
                    {errors.email && <span className="text-red-500 text-sm mt-1 block">{errors.email.message}</span>}
                  </div>
              </div>
            </div>
            <button onClick={handleNextStep} type="button" className="btn-primary w-full flex justify-center gap-2">
              Próximo Passo <ChevronRight size={20} />
            </button>
          </motion.div>
        );
      case 2:
        return (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} key="step2">
            <h4 className="text-xl font-heading text-brand-slate mb-4">Passo 2: Qual a área do seu caso?</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
              {CONTACT_AREAS.map(areaOption => (
                <button
                  key={areaOption}
                  type="button"
                  onClick={() => {
                      setValue('area', areaOption);
                      trigger('area'); // Revalida para tirar o erro se houver
                  }}
                  className={`p-4 border rounded-xl text-left transition-colors ${selectedArea === areaOption ? 'border-brand-accent bg-brand-accent/5' : 'border-brand-slate/20 hover:border-brand-slate/50'}`}
                  aria-label={`Selecionar área ${areaOption}`}
                  aria-pressed={selectedArea === areaOption}
                >
                  <span className={`font-semibold ${selectedArea === areaOption ? 'text-brand-accent' : 'text-brand-slate'}`}>{areaOption}</span>
                </button>
              ))}
            </div>
            {errors.area && <span className="text-red-500 text-sm block mb-4">{errors.area.message}</span>}
            
            <div className="flex gap-4 mt-4">
              <button onClick={prevStep} type="button" className="btn-secondary flex-1 flex justify-center gap-2">
                <ChevronLeft size={20} /> Voltar
              </button>
              <button onClick={handleNextStep} type="button" className="btn-primary flex-1 flex justify-center gap-2">
                Próximo Passo <ChevronRight size={20} />
              </button>
            </div>
          </motion.div>
        );
      case 3:
        return (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} key="step3">
            <h4 className="text-xl font-heading text-brand-slate mb-4">Passo 3: Conte um pouco do que aconteceu</h4>
            
            <textarea
              {...register('situation')}
              aria-invalid={errors.situation ? 'true' : 'false'}
              rows={5}
              className={`input resize-none w-full ${errors.situation ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Descreva de forma resumida o motivo do contato..."
              aria-label="Descreva sua situação"
            />
            {errors.situation && <span className="text-red-500 text-sm mt-1 mb-4 block">{errors.situation.message}</span>}

            {/* Honeypot Invisível para Bots */}
            <input type="text" {...register('honeypot')} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

            <div className="flex items-start gap-3 mt-4 mb-6">
              <input 
                type="checkbox" 
                id="consent" 
                {...register('consent')}
                className="mt-1" 
                aria-label="Consentimento para contato"
              />
              <label htmlFor="consent" className="text-sm text-brand-ink">Li e concordo com a Política de Privacidade e autorizo o contato.</label>
            </div>
            {errors.consent && <span className="text-red-500 text-sm -mt-4 mb-4 block">{errors.consent.message}</span>}

            <div className="flex flex-col sm:flex-row gap-3 mb-2">
                <button onClick={prevStep} type="button" className="btn-secondary flex-1 text-sm justify-center gap-1">
                  <ChevronLeft size={16} /> Voltar
                </button>
                <button 
                  type="button" 
                  onClick={handleSubmit((data) => onSubmit(data, undefined, 'email'))} 
                  disabled={isSubmitting} 
                  className="btn-secondary flex-1 text-sm justify-center gap-1"
                >
                  {isSubmitting && submitType === 'email' ? 'Enviando...' : <><Send size={16} /> E-mail</>}
                </button>
                <button 
                  type="button" 
                  onClick={handleSubmit((data) => onSubmit(data, undefined, 'whatsapp'))} 
                  disabled={isSubmitting} 
                  className="btn-primary flex-1 text-sm justify-center bg-green-600 border-green-600 hover:bg-green-700 hover:border-green-700 gap-1"
                >
                  {isSubmitting && submitType === 'whatsapp' ? 'Enviando...' : <><MessageCircle size={16} /> WhatsApp</>}
                </button>
            </div>

            {submitStatus === 'error' && (
              <p className="text-red-500 text-sm mt-4 text-center">Erro ao enviar. Tente novamente.</p>
            )}
          </motion.div>
        );
      case 4:
         return (
             <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8" key="step4">
                 <CheckCircle className="text-green-500 w-16 h-16 mx-auto mb-4" />
                 <h4 className="text-2xl font-semibold text-brand-slate mb-2">Mensagem Enviada!</h4>
                 <p className="text-brand-ink/80 mb-6">Obrigado por nos contar sobre a sua situação. Nossa equipe entrará em contato em breve.</p>
                 <button onClick={() => setStep(1)} className="btn-secondary">
                     Enviar outra solicitação
                 </button>
             </motion.div>
         )
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-brand-sand pt-32">
      <div className="wrapper py-16">
        <motion.div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-heading text-brand-slate mb-6">Triagem e Atendimento</h1>
            <p className="text-lg text-brand-ink/90 max-w-2xl mx-auto">
              Responda estas rápidas perguntas em 3 passos para que nossa equipe já prepare a melhor solução.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="card card-pad shadow-lg border-brand-slate/5">
                <div className="flex mb-8">
                    {[1,2,3].map(s => (
                        <div key={s} className={`flex-1 h-2 mx-1 rounded-full ${step >= s ? 'bg-brand-accent' : 'bg-brand-slate/10'}`} />
                    ))}
                </div>
                <AnimatePresence mode="wait">
                    {renderStepContent()}
                </AnimatePresence>
            </div>

            <div className="card card-pad bg-white/50 backdrop-blur-sm">
                <h3 className="text-2xl font-heading text-brand-slate mb-8">Informações de Contato</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-accent mt-1" aria-hidden="true" />
                    <div><strong className="text-brand-slate">Atendimento Jundiaí e Região</strong></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-accent mt-1" aria-hidden="true" />
                    <div><strong className="text-brand-slate">Horário: {businessHoursText}</strong></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-brand-accent mt-1" aria-hidden="true" />
                    <div><strong className="text-brand-slate">{settings?.contact_email || process.env.REACT_APP_CONTACT_EMAIL || 'contato@luaraadvocacia.com.br'}</strong></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-brand-accent mt-1" aria-hidden="true" />
                    <div><strong className="text-brand-slate">WhatsApp: {settings?.whatsapp_display || process.env.REACT_APP_WHATSAPP_DISPLAY || '(11) 99999-9999'}</strong></div>
                  </div>
                </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
export default Contact;